// dashboardArt.js — per-brand dashboard illustration paths.
//
// WHY THIS EXISTS
// The dashboard art was six hardcoded paths in AdminDashboard.js, shared by both
// brands. Three of the six carry TRADE iconography — a toolbox with a wrench and
// ratchet (hero), a wrench on a clipboard (jobs), and a hardhat with an ID badge
// (employees) — so an SMBMate accountant opened their dashboard to a toolbox. The
// other three (coins, gauge, invoice) are already subject-neutral.
//
// All six are also rendered in TradesMatePro blue, so even the neutral ones are
// off-brand for SMBMate's teal. Subject and colour are two separate axes and both
// have to move; swapping only the subject would still leave a blue dashboard.
//
// THE LAYOUT
//   /assets/dashboard/<brand-dir>/<name>.webp   preferred, per brand
//   /assets/dashboard/<name>.webp               legacy shared files (today's art)
//
// Nothing here checks whether a file exists — the browser does, via the onError
// fallback in ModernPageHeader. That means this module can be shipped BEFORE the new
// artwork: every brand keeps rendering the current images until real per-brand files
// are dropped into their directory, and switches over the moment they are. No code
// change, no deploy, no broken-image window.

const ART_NAMES = ['hero', 'employees', 'jobs', 'revenue', 'completion', 'receivables'];

// The legacy filenames are not renamed. They are referenced by the fallback below,
// so renaming them would break every brand that has not shipped art yet.
const LEGACY_FILES = {
  hero: 'field-operations-hero',
  employees: 'active-employees',
  jobs: 'active-jobs',
  revenue: 'cash-revenue',
  completion: 'completion-rate',
  receivables: 'outstanding-ar'
};

// Keyed by BRAND_CONFIG.code (brandingUtils), NOT by hostname — the dashboard is
// behind auth, where the company's brand wins over whatever domain was typed.
const BRAND_ART_DIRS = {
  tradesmate: 'tradesmatepro',
  smbmate: 'smbmate'
};

const base = () => `${process.env.PUBLIC_URL}/assets/dashboard`;

/** Legacy shared path for one art key — the fallback target. */
export function legacyArtSrc(name) {
  return `${base()}/${LEGACY_FILES[name]}.webp`;
}

/**
 * Preferred per-brand path, or null when the brand has no directory mapped (in which
 * case callers should use the legacy path directly rather than requesting a 404).
 */
export function brandArtSrc(brandCode, name) {
  const dir = BRAND_ART_DIRS[brandCode];
  if (!dir || !LEGACY_FILES[name]) return null;
  return `${base()}/${dir}/${name}.webp`;
}

/**
 * All six art entries for a brand, each as { src, fallbackSrc }.
 *
 * fallbackSrc is null when src IS the legacy path — passing the same URL as its own
 * fallback would make a genuinely missing file retry itself forever.
 */
export function getDashboardArt(brandCode) {
  return ART_NAMES.reduce((acc, name) => {
    const branded = brandArtSrc(brandCode, name);
    const legacy = legacyArtSrc(name);
    acc[name] = branded
      ? { src: branded, fallbackSrc: legacy }
      : { src: legacy, fallbackSrc: null };
    return acc;
  }, {});
}
